import React, { useRef, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const TextOverlay = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  transition: opacity 0.5s ease;
  color: #13182a;
`;
// Old version with issue on mobile
// const HeroTitle = styled.h1`
//     font-size: 2.5rem;
//     font-weight: 500;
//     font-family: 'Garamond', serif;
//     color: #333;

//     @media (max-width: 900px) {
//         font-size: 2.0rem;
//     }
// `;

const HeroTitle = styled.h1`
  font-size: clamp(1.5rem, 5vw, 2.5rem);
  font-weight: 500;
  font-family: 'Garamond', serif;
  color: #13182a;

  @media (max-width: 748px) {
    // font-size: clamp(1.2rem, 4vw, 2.0rem);
    font-size: clamp(1.6rem, 4.5vw, 2.5rem);
  }
`;

interface SubHeroProps {
  title: string;
}

/**
 * Animated Heaviside step function: (-2,2) y-range, full header width.
 * Calls onComplete when fully drawn.
 * @param segment1Duration - Duration in seconds to draw Segment 1
 * @param segment2Duration - Duration in seconds to draw Segment 2
 * @param segment3Duration - Duration in seconds to draw Segment 3
 */
function HeavisideLine({
  onComplete,
  segment1Duration = 1,
  segment2Duration = 0.5,
  segment3Duration = 1,
}: {
  onComplete: () => void;
  segment1Duration?: number;
  segment2Duration?: number;
  segment3Duration?: number;
}) {
  const { geometry, lineObject, segmentCounts } = useMemo(() => {
    // Camera frustum dimensions
    const fov = 50;
    const cameraZ = 10;
    const fovRad = (fov * Math.PI) / 180;
    const worldHeight = 2 * Math.tan(fovRad / 2) * cameraZ;
    const aspect = window.innerWidth / window.innerHeight;
    const worldWidth = worldHeight * aspect;

    // Map header max-width (1200px) to world units
    const pixelsPerUnit = window.innerWidth / worldWidth;
    const paragraphWidthPx = 1200;
    const xRange = paragraphWidthPx / pixelsPerUnit;

    // Desired y-range
    const yLow = -1.5;
    const yHigh = 1.5;

    // Build points
    const points: THREE.Vector3[] = [];
    // Segment 1: left horizontal at yLow
    const segment1Points: THREE.Vector3[] = [];
    for (let x = -xRange; x <= 0; x += paragraphWidthPx / pixelsPerUnit / 100) {
      segment1Points.push(new THREE.Vector3(x, yLow, 0));
    }
    points.push(...segment1Points);

    // Segment 2: vertical at x=0
    const segment2Points: THREE.Vector3[] = [];
    for (let y = yLow; y <= yHigh; y += (yHigh - yLow) / 100) {
      segment2Points.push(new THREE.Vector3(0, y, 0));
    }
    points.push(...segment2Points);

    // Segment 3: right horizontal at yHigh
    const segment3Points: THREE.Vector3[] = [];
    for (let x = 0; x <= xRange; x += paragraphWidthPx / pixelsPerUnit / 100) {
      segment3Points.push(new THREE.Vector3(x, yHigh, 0));
    }
    points.push(...segment3Points);

    // Create line
    const geom = new THREE.BufferGeometry().setFromPoints(points);
    geom.setDrawRange(0, 0);
    const mat = new THREE.LineBasicMaterial({
      color: '#13182a',
      linewidth: 2, // ← increase this value to make the segments thicker
    });
    const line = new THREE.Line(geom, mat);

    return {
      geometry: geom,
      lineObject: line,
      segmentCounts: {
        segment1: segment1Points.length,
        segment2: segment2Points.length,
        segment3: segment3Points.length,
      },
    };
  }, []);

  const maxCount = geometry.attributes.position.count;
  const drawCount = useRef(0);
  const done = useRef(false);

  useFrame((_, delta) => {
    if (drawCount.current >= maxCount) {
      if (!done.current) {
        done.current = true;
        onComplete();
      }
      return;
    }

    // Calculate points to draw based on current segment
    let increment = 0;
    const currentCount = drawCount.current;

    if (currentCount < segmentCounts.segment1) {
      // Segment 1: Draw over segment1Duration
      const pointsPerSecond = segmentCounts.segment1 / segment1Duration;
      increment = Math.ceil(pointsPerSecond * delta);
    } else if (currentCount < segmentCounts.segment1 + segmentCounts.segment2) {
      // Segment 2: Draw over segment2Duration
      const pointsPerSecond = segmentCounts.segment2 / segment2Duration;
      increment = Math.ceil(pointsPerSecond * delta);
    } else {
      // Segment 3: Draw over segment3Duration
      const pointsPerSecond = segmentCounts.segment3 / segment3Duration;
      increment = Math.ceil(pointsPerSecond * delta);
    }

    drawCount.current = Math.min(maxCount, drawCount.current + increment);
    geometry.setDrawRange(0, drawCount.current);
  });

  // eslint-disable-next-line react/no-unknown-property
  return <primitive object={lineObject} />;
}

// Define defaultProps to satisfy react/require-default-props
HeavisideLine.defaultProps = {
  segment1Duration: 1,
  segment2Duration: 0.5,
  segment3Duration: 1,
};

const SubHero: React.FC<SubHeroProps> = ({ title }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [textPos, setTextPos] = useState({ left: '50%', top: '50%' });
  const [lineDrawn, setLineDrawn] = useState(false);

  useEffect(() => {
    const updatePos = () => {
      if (!containerRef.current || !textRef.current) return;
      const cw = containerRef.current.clientWidth;
      const ch = containerRef.current.clientHeight;

      // Map world coords to screen
      const fov = 50;
      const cameraZ = 10;
      const fovRad = (fov * Math.PI) / 180;
      const worldHeight = 2 * Math.tan(fovRad / 2) * cameraZ;
      const aspect = cw / ch;
      const worldWidth = worldHeight * aspect;
      const pixelsPerUnit = cw / worldWidth;

      // horizontal segment at yLow = -2
      const yLow = -1.5;
      const screenCenterX = cw / 2;
      const yLowScreenPx = ch / 2 - yLow * pixelsPerUnit;

      // text dims
      const rem = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const tw = textRef.current.offsetWidth;
      const th = textRef.current.offsetHeight;

      // right edge 1rem left of x=0 center
      const leftPx = screenCenterX - rem - tw;
      // bottom 1rem above horizontal line
      const topPx = yLowScreenPx - rem - th;

      setTextPos({
        left: `${(leftPx / cw) * 100}%`,
        top: `${(topPx / ch) * 100}%`,
      });
    };

    updatePos();
    window.addEventListener('resize', updatePos);
    return () => window.removeEventListener('resize', updatePos);
  }, [title, lineDrawn]);

  return (
    <HeroContainer ref={containerRef}>
      <Canvas
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
        camera={{ position: [0, 0, 10], fov: 50 }}
      >
        <HeavisideLine
          onComplete={() => setLineDrawn(true)}
          segment1Duration={1.5} // Adjust this to change Segment 1 speed (in seconds)
          segment2Duration={0.25} // Adjust this to change Segment 2 speed (in seconds)
          segment3Duration={1.5} // Adjust this to change Segment 3 speed (in seconds)
        />
      </Canvas>

      <TextOverlay
        ref={textRef}
        style={{
          left: textPos.left,
          top: textPos.top,
          opacity: lineDrawn ? 1 : 0,
        }}
      >
        <HeroTitle>{title}</HeroTitle>
      </TextOverlay>
    </HeroContainer>
  );
};

export default SubHero;
